import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { Loading } from "@Components/Loading";
import * as stylex from "@stylexjs/stylex";

import type { ICategoriesCarouselItem } from "../common/CategoriesCarousel";
import { CategoryLink } from "../common/links/CategoryLink";

import * as css from "./Moment.module.css";

export interface IMomentMapBrandsFilter extends IComponentAjax<ICategoriesCarouselItem[]> {
    selectedCategory: string;
    onFilterChange: (category: string) => void;
    allFiltersText: string;
}

interface IMomentMapBrandsFilterState extends IComponentAjaxState {
    categories: string[];
}

const styles = stylex.create({
    inlineTable: {
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
    },
});

export class MomentMapBrandsFilter extends ComponentAjax<
    ICategoriesCarouselItem[],
    IMomentMapBrandsFilter,
    IMomentMapBrandsFilterState
> {
    private get categoriesInline() {
        if (!this.state.categories || this.state.categories.length === 0) {
            return null;
        }

        return (
            <div {...stylex.props(styles.inlineTable)}>
                {this.state.categories.map(this.renderCategoryButton)}
            </div>
        );
    }

    public static readonly defaultProps: IMomentMapBrandsFilter = {
        ...ComponentAjax.defaultProps,
        selectedCategory: null,
        onFilterChange: null,
        allFiltersText: null,
    };

    constructor(props: IMomentMapBrandsFilter) {
        super(props);

        this.state = {
            isLoading: this.props.initialData ? this.props.initialData.length === 0 : true,
            categories: this.setCategoriesList(this.props.initialData),
        };
    }

    public componentDidMount() {
        if (this.state.isLoading === false || this.props.initialData) {
            return;
        }

        this.ajaxRequest();
    }

    public render() {
        return (
            <div {...this.rootDefaultAttributes(css.momentMapBrandsFilterComponent)}>
                {this.state.isLoading && <Loading />}
                {this.categoriesInline}
                {this.renderAjaxErrorContainer()}
            </div>
        );
    }

    protected ajaxSuccess(response: ICategoriesCarouselItem[]): void {
        if (!response) {
            this.setState({ isLoading: false });
            return;
        }

        const categories = this.setCategoriesList(response);

        this.setState({ isLoading: false, categories });
    }

    protected ajaxFail() {
        this.setState({ isLoading: false });
        return true;
    }

    private setCategoriesList(initialData: ICategoriesCarouselItem[]): string[] {
        if (!initialData) {
            return null;
        }

        const categories = initialData.map((item) => item.name);

        if (this.props.allFiltersText) {
            categories.splice(0, 0, this.props.allFiltersText);
        }

        return categories;
    }

    private categoryClick(category: string, i: number): void {
        if (i === 0 && !this.props.selectedCategory) {
            return;
        }

        if (this.props.selectedCategory === category) {
            return;
        }

        if (i === 0) {
            this.props.onFilterChange.call(this, null);
            return;
        }

        this.props.onFilterChange.call(this, category);
    }

    private renderCategoryButton = (category: string, i: number): JSX.Element => {
        const onClick = () => {
            this.categoryClick(category, i);
        };

        let active = category === this.props.selectedCategory;
        if (!this.props.selectedCategory) {
            active = i === 0;
        }

        return <CategoryLink key={`${category}-${i}`} onClick={onClick} active={active} text={category} />;
    };
}
